/* eslint-disable react/destructuring-assignment */
import React, { useEffect, useState } from 'react';
import { withRouter, useHistory, useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { connect } from 'react-redux';
import { withFormik, Field, Form } from 'formik';
import { Col, Row } from 'react-bootstrap';
import { FormikInput, FormikRadio } from '../../components';
import OnboardingHeader from './OnboardingHeader';
import landingHeroBg from '../../assets/images/index-bg-3.png';
import ParrotLoader from '../../components/Utils/Spinner/ParrotLoader';
import testimonialCards from '../../data/testimonials.json';
import { createAccountNameRequest } from '../../requests/accountName';
import { updateOnboardingRegistrationRequest } from '../../requests/onboardingRegistration';
import { initOnboardingSubscriptionRequest, redirectToTransbankForm } from '../../requests/onboardingPayment';
import { setAccountName } from '../../actions/account';
import OnboardingStepper from '../../components/Onboarding/OnboardingStepper';
import {
  clearOnboardingPaymentContext,
  loadOnboardingPaymentContext,
  findPlatformPlan
} from '../../data/platformPlans';

const STAR_RATINGS = [1, 2, 3, 4, 5];

// The paid onboarding is Chile-only: the +56 lives in the input's visual
// prefix and is (re)applied once when building the payload, so the user can
// paste "+56 9 ..." without ending up with a doubled code.
const stripChileanPrefix = value => value.replace(/^\s*\+?56\s*/, '').replace(/[^\d\s]/g, '');

const toChileanPhone = value => {
  const digits = stripChileanPrefix(value || '').replace(/\D/g, '');
  return digits ? `+56${digits}` : '';
};

const AccountRegister = props => {
  const { isValid, errors, touched, values, location, dispatch } = props;
  const isAccounting = location?.state?.isAccounting;
  const isFreemium = location?.state?.isFreemium;
  const isTwistGo = location?.state?.isTwistGo;
  const paymentContext = loadOnboardingPaymentContext();
  const withPaymentFlow = Boolean(
    location?.state?.withPaymentFlow || paymentContext?.platformPlan || paymentContext?.paymentConfirmed
  );
  const requiresPayment = withPaymentFlow && !isAccounting && !isFreemium;
  const selectedPlan = requiresPayment ? findPlatformPlan(paymentContext?.platformPlan) : null;

  const backPathname = () => {
    if (isFreemium) return '/freemium';
    if (isAccounting) return '/accounting';
    if (isTwistGo) return '/twistgo';
    if (requiresPayment) return '/onboarding/plans';
    return '/manual_onboarding';
  };
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(window.innerWidth <= 768 ? 1 : 2);
  const [isLoading, setIsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Check if iframe mode is enabled
  const currentLocation = useLocation();
  const searchParams = new URLSearchParams(currentLocation.search);
  const isIframeMode = searchParams.get('format') === 'iframe';

  const landingHeroStyle = !isIframeMode ? { '--landing-hero-bg-url': `url(${landingHeroBg})` } : {};

  const account = React.useMemo(() => props.account || {}, [props.account]);

  const history = useHistory();
  const isAble =
    values.firstname &&
    values.firstname.length &&
    values.lastname &&
    values.lastname.length &&
    values.email &&
    values.email.length &&
    values.phone &&
    values.phone.length &&
    (requiresPayment || values.serviceTier !== null) &&
    isValid &&
    (!isFreemium || termsAccepted);

  useEffect(() => {
    if (!requiresPayment) return;

    // This step now runs BEFORE the card inscription (D2): it only needs a
    // registration with a chosen plan.
    if (!paymentContext?.registrationId || !paymentContext?.platformPlan) {
      history.replace({
        pathname: paymentContext?.registrationId ? '/onboarding/plans' : '/onboarding',
        search: isIframeMode ? '?format=iframe' : ''
      });
      return;
    }

    if (!account.accountName && paymentContext.accountName) {
      dispatch(
        setAccountName({
          ...account,
          accountName: paymentContext.accountName,
          companyName: paymentContext.companyName,
          companyRUT: paymentContext.companyRUT,
          country: paymentContext.country || 'Chile',
          email: paymentContext.email,
          twistgo: paymentContext.twistgo
        })
      );
    }
  }, [requiresPayment, history, isIframeMode, account, dispatch]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setPageSize(mobile ? 1 : 2);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const totalPagesForSize = Math.max(1, Math.ceil(testimonialCards.length / pageSize));
    setPageIndex(prev => (prev >= totalPagesForSize ? totalPagesForSize - 1 : prev));
  }, [pageSize]);

  // Paid flow shows a single testimonial card that auto-rotates: fades out
  // downwards, swaps content, then eases back up into place.
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [testimonialLeaving, setTestimonialLeaving] = useState(false);
  useEffect(() => {
    if (!requiresPayment || testimonialCards.length < 2) return undefined;

    const interval = setInterval(() => {
      setTestimonialLeaving(true);
      setTimeout(() => {
        setTestimonialIndex(prev => (prev + 1) % testimonialCards.length);
        setTestimonialLeaving(false);
      }, 380);
    }, 8000);
    return () => clearInterval(interval);
  }, [requiresPayment]);
  const rotatingTestimonial = testimonialCards[testimonialIndex];

  const totalPages = Math.max(1, Math.ceil(testimonialCards.length / pageSize));
  const visibleTestimonials = testimonialCards.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize);
  const placeholderCount = Math.max(0, pageSize - visibleTestimonials.length);
  const showVideo = isFreemium && !isIframeMode;

  const handleNextTestimonial = () => {
    setPageIndex(prev => (prev + 1) % totalPages);
  };

  const handlePrevTestimonial = () => {
    setPageIndex(prev => (prev - 1 + totalPages) % totalPages);
  };

  const buildAccountPayload = (overrides = {}) => ({
    firstname: values.firstname,
    lastname: values.lastname,
    email: values.email,
    account_domain: account.accountName,
    company_name: account.companyName,
    company_rut: account.companyRUT,
    country: account.country || 'Chile',
    phone_number: values.phone,
    service_tier: overrides.serviceTier ?? values.serviceTier,
    twistgo: account.twistgo
  });

  // Paid flow (D2): personal data is saved on the server-side registration and
  // THEN the card inscription starts - Transbank is the last step. No amounts
  // travel from the browser; the return_url carries the registration uuid so
  // the confirm page works in any browser/window.
  const handlePaidSubmit = () => {
    if (isLoading) return; // double-click guard: one in-flight submit at a time
    setIsLoading(true);
    const returnUrl = `${window.location.origin}/onboarding/payment/confirm${isIframeMode ? '?format=iframe' : ''}`;

    updateOnboardingRegistrationRequest(paymentContext.registrationId, {
      dispatch,
      params: {
        registration: {
          firstname: values.firstname,
          lastname: values.lastname,
          email: values.email,
          phone_number: toChileanPhone(values.phone)
        }
      },
      successCallback: () => {
        initOnboardingSubscriptionRequest({
          dispatch,
          params: { registration_uuid: paymentContext.registrationId, return_url: returnUrl },
          successCallback: response => {
            const { token, service_form_url: serviceFormUrl } = response.data || {};
            if (!token || !serviceFormUrl) {
              setIsLoading(false);
              history.push({ pathname: '/failure', search: isIframeMode ? '?format=iframe' : '' });
              return;
            }
            redirectToTransbankForm({ serviceUrl: serviceFormUrl, token });
          },
          failureCallback: () => {
            setIsLoading(false);
            history.push({ pathname: '/failure', search: isIframeMode ? '?format=iframe' : '' });
          }
        });
      },
      failureCallback: () => {
        setIsLoading(false);
        history.push({ pathname: '/failure', search: isIframeMode ? '?format=iframe' : '' });
      }
    });
  };

  const handleSubmit = () => {
    if (requiresPayment) {
      if (!paymentContext?.registrationId) {
        history.push({ pathname: '/failure', search: isIframeMode ? '?format=iframe' : '' });
        return;
      }
      handlePaidSubmit();
      return;
    }

    setIsLoading(true);

    createAccountNameRequest({
      params: { account_name: buildAccountPayload() },
      dispatch,
      successCallback: response => {
        const tenantCreated = response?.data?.result === true || response?.data?.result === 'true';
        if (!tenantCreated) {
          setIsLoading(false);
          history.push({
            pathname: '/failure',
            search: isIframeMode ? '?format=iframe' : ''
          });
          return;
        }

        clearOnboardingPaymentContext();
        setIsLoading(false);
        history.push({
          pathname: '/success',
          search: isIframeMode ? '?format=iframe' : ''
        });
      },
      failureCallback: () => {
        setIsLoading(false);
        history.push({
          pathname: '/failure',
          search: isIframeMode ? '?format=iframe' : ''
        });
      }
    });
  };

  return (
    <div
      className={isIframeMode ? 'onboarding-page onboarding-page--iframe' : 'onboarding-page landing-hero'}
      style={!isIframeMode ? landingHeroStyle : undefined}
    >
      {!isIframeMode && (
        <>
          <div className="landing-hero__bg" />
          <div className="landing-hero__wash" />
          <div className="landing-hero__darken" />
          <div className="landing-hero__tint" />
        </>
      )}
      <div
        className={
          isIframeMode
            ? 'onboarding-page__content d-flex align-items-center justify-content-between'
            : 'landing-hero__content d-flex align-items-center justify-content-between'
        }
      >
        <Row className="justify-content-center align-items-start w-100" noGutters>
          <Col xs={12} md={isIframeMode ? 12 : 4} className="pl-md-2 pr-2 pl-2 col-md-4 col-12">
            <div className="onboarding-card-stack d-flex flex-column mt-2">
              {!isIframeMode && (
                <div className="onboarding-card-stack__logo mb-1">
                  <OnboardingHeader isIframeMode={isIframeMode} />
                </div>
              )}

              {!isIframeMode && (
                <div className="d-md-none onboarding-mobile-hero mb-3">
                  <h2 className="users-title font-weight-bold mb-1 text-center">Haz que tu negocio no dependa de ti</h2>
                  <p className="m-0 text-center onboarding-mobile-hero__subtitle">
                    Conecta SII, bancos y previred para empezar a delegar las finanzas de tu negocio
                  </p>
                  {showVideo && (
                    <section className="onboarding-right__video w-100 mx-auto mt-3">
                      <div className="onboarding-right__videoWrapper">
                        <div className="onboarding-right__videoStrip" aria-hidden="true">
                          <svg
                            className="onboarding-right__videoStrip-icon"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path d="M8 5v14l11-7L8 5zm2 5.07l4.44 2.51-4.44 2.52v-5.03z" />
                          </svg>
                          <span className="onboarding-right__videoStrip-text">Mira cómo funciona (1 min)</span>
                        </div>
                        <iframe
                          src="https://www.loom.com/embed/dff2445ceb5d4aca9f7c88f80faa01d6"
                          title="Parrotfy Freemium demo"
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                          loading="lazy"
                        />
                      </div>
                    </section>
                  )}
                </div>
              )}

              <div className={isIframeMode ? 'iframe-form' : 'onboarding-card'}>
                {!isIframeMode && (
                  <div className="d-md-none onboarding-card__intro text-center">
                    <h3 className="font-weight-bold mb-2 text-center">Completa los datos de tu perfil</h3>
                  </div>
                )}

                {!isIframeMode && (
                  <div className="d-none d-md-block onboarding-card__intro">
                    {requiresPayment && <OnboardingStepper current={3} />}
                    <h1 className="pf-card__title mb-1">Completa los datos de tu perfil</h1>
                    <p className="pf-card__subtitle m-0 mb-2">
                      Usaremos estos datos para tu cuenta y para contactarte si lo necesitas.
                    </p>
                  </div>
                )}
                {isLoading ? (
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <ParrotLoader title="Estamos activando tu cuenta, ¡Ya no falta nada!" />
                  </div>
                ) : (
                  <Form className="onboarding-card__form">
                    <div className="onboarding-card__field mb-3">
                      <Field name="firstname">
                        {({ field }) => (
                          <FormikInput
                            {...field}
                            abbr
                            label="Nombre"
                            placeholder="John"
                            inputType="text"
                            error={errors[field.name]}
                            touched={touched[field.name]}
                          />
                        )}
                      </Field>
                    </div>

                    <div className="onboarding-card__field mb-3">
                      <Field name="lastname">
                        {({ field }) => (
                          <FormikInput
                            {...field}
                            abbr
                            label="Apellido"
                            placeholder="Doe"
                            inputType="text"
                            error={errors[field.name]}
                            touched={touched[field.name]}
                          />
                        )}
                      </Field>
                    </div>

                    {/* Paid flow: the email was already captured at the plan
                        step (it lives on the registration) - re-showing it here
                        only confuses. Formik still submits values.email. */}
                    {!requiresPayment && (
                      <div className="onboarding-card__field mb-3">
                        <Field name="email">
                          {({ field }) => (
                            <FormikInput
                              {...field}
                              abbr
                              label="E-mail"
                              placeholder="john.doe@parrotfy.com"
                              inputType="email"
                              error={errors[field.name]}
                              touched={touched[field.name]}
                            />
                          )}
                        </Field>
                      </div>
                    )}

                    <div className="onboarding-card__field mb-3">
                      <div className="form-group col">
                        <Field name="phone">
                          {({ field }) =>
                            requiresPayment ? (
                              <>
                                <span className="pf-label">
                                  Teléfono <abbr className="text-danger">*</abbr>
                                </span>
                                <div className="pf-field-group pf-field-group--prefix">
                                  <span className="pf-field-group__prefix">
                                    <span role="img" aria-label="Chile">
                                      🇨🇱
                                    </span>{' '}
                                    +56
                                  </span>
                                  <input
                                    {...field}
                                    type="tel"
                                    placeholder="9 1234 5678"
                                    onChange={e =>
                                      field.onChange({
                                        target: { name: field.name, value: stripChileanPrefix(e.target.value) }
                                      })
                                    }
                                  />
                                </div>
                                {errors.phone && touched.phone && (
                                  <span className="pf-help text-danger">{errors.phone}</span>
                                )}
                              </>
                            ) : (
                              <FormikInput
                                {...field}
                                abbr
                                label="Teléfono"
                                placeholder="68073261"
                                inputType="text"
                                error={errors[field.name]}
                                touched={touched[field.name]}
                              />
                            )
                          }
                        </Field>
                      </div>
                    </div>

                    {/* Twist GO accounts always provision Business (forced server-side),
                        so the service selector is hidden for them. */}
                    {!isAccounting && !isFreemium && !requiresPayment && !isTwistGo && (
                      <div className="onboarding-card__field mb-3">
                        <Field name="serviceTier">
                          {({ field, form }) => (
                            <FormikRadio
                              field={field}
                              form={form}
                              label="Tipo de servicio"
                              abbr
                              options={[
                                { value: 1, label: 'Starter' },
                                { value: 2, label: 'PRO' },
                                { value: 0, label: 'Starter + Contabilidad' }
                              ]}
                              error={form.errors[field.name]}
                              touched={form.touched[field.name]}
                            />
                          )}
                        </Field>
                      </div>
                    )}

                    {isFreemium && (
                      <div className="onboarding-card__field mt-2 mb-0">
                        <div className="form-check">
                          <label className="form-check-label" htmlFor="termsAccepted">
                            <input
                              id="termsAccepted"
                              type="checkbox"
                              className="form-check-input"
                              checked={termsAccepted}
                              onChange={e => setTermsAccepted(e.target.checked)}
                            />
                            He leído y acepto los{' '}
                            <a
                              href="https://www.parrotfy.com/es/terms-of-service"
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#2743B2' }}
                            >
                              Términos y condiciones
                            </a>
                          </label>
                        </div>
                      </div>
                    )}

                    <div className="onboarding-card__form-actions d-flex flex-column align-items-center">
                      <button
                        type="button"
                        className="btn pf-btn pf-btn--gradient pf-btn--block"
                        disabled={!isAble || isLoading}
                        onClick={handleSubmit}
                      >
                        {requiresPayment ? 'Continuar al pago →' : 'Crear cuenta'}
                      </button>
                      <button
                        type="button"
                        className="btn pf-btn pf-btn--ghost mt-2"
                        onClick={() =>
                          history.push({
                            pathname: backPathname(),
                            search: isIframeMode ? '?format=iframe' : ''
                          })
                        }
                      >
                        ← Volver
                      </button>
                    </div>
                    {requiresPayment && (
                      <p className="pf-help text-center mt-2 mb-0">
                        Al continuar te llevaremos al portal seguro de Transbank para inscribir tu tarjeta.
                      </p>
                    )}
                  </Form>
                )}
              </div>

              {!isIframeMode && (
                <div className="d-none onboarding-mobile-testimonials mt-4">
                  <div className="onboarding-right__carousel-area">
                    <h3 className="text-center users-title mb-3 mt-3 font-weight-bold">
                      ¿Qué dicen nuestros usuarios?
                    </h3>
                    <div className="onboarding-right__carousel-track mt-3 d-flex align-items-center justify-content-between w-100">
                      <button
                        type="button"
                        onClick={handlePrevTestimonial}
                        aria-label="Testimonio anterior"
                        className="onboarding-right__carousel-button"
                      >
                        <span className="onboarding-right__carousel-icon">‹</span>
                      </button>
                      <div className="onboarding-right__testimonial-viewport">
                        {visibleTestimonials.map(card => (
                          <article
                            key={`testimonial-${card.name}-${card.company}`}
                            className="onboarding-right__testimonial mx-auto"
                          >
                            <div className="onboarding-right__testimonial-stars" aria-hidden="true">
                              {STAR_RATINGS.map(value => (
                                <span key={`star-${card.name}-${card.company}-${value}`} role="presentation">
                                  ★
                                </span>
                              ))}
                            </div>
                            <p className="onboarding-right__testimonial-quote m-0">&ldquo;{card.quote}&rdquo;</p>
                            <div className="onboarding-right__testimonial-meta mt-2">
                              <p className="mb-0">{card.name}</p>
                              <p className="mb-0">
                                {card.role}
                                {card.company ? `, ${card.company}` : ''}
                              </p>
                            </div>
                          </article>
                        ))}
                        {Array.from({ length: placeholderCount }, (_, index) => (
                          <article
                            key={`placeholder-${pageIndex}-${index}`}
                            className="onboarding-right__testimonial onboarding-right__testimonial-placeholder"
                            aria-hidden="true"
                          />
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={handleNextTestimonial}
                        aria-label="Testimonio siguiente"
                        className="onboarding-right__carousel-button"
                      >
                        <span className="onboarding-right__carousel-icon">›</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Col>

          {!isIframeMode && (
            <Col
              xs={12}
              md={8}
              className="onboarding-right-col d-none d-md-flex align-items-stretch justify-content-center"
            >
              <div className="onboarding-right d-flex flex-column">
                <div className="onboarding-right__intro text-center">
                  <h2 className="users-title font-weight-bold mb-1">
                    {requiresPayment ? 'Casi listo para empezar' : 'Haz que tu negocio no dependa de ti'}
                  </h2>
                  <p className="m-0">
                    {requiresPayment
                      ? 'Solo necesitamos unos datos tuyos antes de inscribir tu tarjeta.'
                      : 'Conecta SII, bancos y previred para empezar a delegar las finanzas de tu negocio'}
                  </p>
                </div>

                {requiresPayment && selectedPlan && (
                  <div className="pf-right-stack mt-3">
                    <div className="pf-plan-selected">
                      <div className="pf-plan-selected__left">
                        <span className="pf-plan-selected__icon">✓</span>
                        <div>
                          <div className="pf-plan-selected__kicker">Plan seleccionado</div>
                          <div className="pf-plan-selected__name">Parrotfy {selectedPlan.name}</div>
                        </div>
                      </div>
                      <div className="pf-plan-selected__price">
                        {selectedPlan.priceLabel}
                        <small>/mes · neto + IVA</small>
                      </div>
                    </div>

                    <div className="pf-next-steps">
                      <h3 className="pf-next-steps__title">Qué sigue después de crear tu cuenta</h3>
                      <div className="pf-next-steps__item">
                        <span className="pf-next-steps__num">1</span>
                        <div>
                          <strong>Inscribe tu tarjeta</strong>
                          <span>Pago seguro procesado por Transbank (OneClick).</span>
                        </div>
                      </div>
                      <div className="pf-next-steps__item">
                        <span className="pf-next-steps__num">2</span>
                        <div>
                          <strong>Conecta el SII y tus bancos</strong>
                          <span>Autoriza el acceso para importar tus datos automáticamente.</span>
                        </div>
                      </div>
                      <div className="pf-next-steps__item">
                        <span className="pf-next-steps__num">3</span>
                        <div>
                          <strong>Conecta Previred</strong>
                          <span>Deja lista la gestión de remuneraciones de tu equipo.</span>
                        </div>
                      </div>
                      <div className="pf-next-steps__secure">
                        <span className="pf-next-steps__secure-icon">
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#2743b1"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <rect x="4" y="11" width="16" height="10" rx="2" />
                            <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                          </svg>
                        </span>
                        Conexión cifrada de extremo a extremo. Nunca almacenamos tus claves.
                      </div>
                    </div>

                    {rotatingTestimonial && (
                      <div className={`pf-testimonial ${testimonialLeaving ? 'pf-testimonial--leaving' : ''}`}>
                        <div className="pf-testimonial__stars" aria-hidden="true">
                          ★★★★★
                        </div>
                        <p className="pf-testimonial__quote">“{rotatingTestimonial.quote}”</p>
                        <div className="pf-testimonial__meta">
                          <span className="pf-testimonial__avatar">
                            {rotatingTestimonial.name
                              .split(' ')
                              .map(word => word[0])
                              .join('')
                              .slice(0, 2)}
                          </span>
                          <div>
                            <div className="pf-testimonial__name">{rotatingTestimonial.name}</div>
                            <div className="pf-testimonial__role">
                              {rotatingTestimonial.role}
                              {rotatingTestimonial.company ? `, ${rotatingTestimonial.company}` : ''}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {showVideo && (
                  <section className="onboarding-right__video w-100 mx-auto">
                    <div className="onboarding-right__videoWrapper">
                      <div className="onboarding-right__videoStrip" aria-hidden="true">
                        <svg
                          className="onboarding-right__videoStrip-icon"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path d="M8 5v14l11-7L8 5zm2 5.07l4.44 2.51-4.44 2.52v-5.03z" />
                        </svg>
                        <span className="onboarding-right__videoStrip-text">Mira cómo funciona (1 min)</span>
                      </div>
                      <iframe
                        src="https://www.loom.com/embed/dff2445ceb5d4aca9f7c88f80faa01d6"
                        title="Parrotfy Freemium demo"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                      />
                    </div>
                  </section>
                )}

                <div className={`onboarding-right__carousel-area ${requiresPayment ? 'd-none' : ''}`}>
                  <h3 className="text-center users-title mb-3 mt-3 font-weight-bold">¿Qué dicen nuestros usuarios?</h3>

                  <div className="onboarding-right__carousel-track mt-3 d-flex align-items-center justify-content-between w-100">
                    <button
                      type="button"
                      onClick={handlePrevTestimonial}
                      aria-label="Testimonio anterior"
                      className="onboarding-right__carousel-button"
                    >
                      ‹
                    </button>

                    <div className="onboarding-right__testimonial-viewport">
                      {visibleTestimonials.map(card => (
                        <article
                          key={`testimonial-${card.name}-${card.company}`}
                          className="onboarding-right__testimonial mx-auto"
                        >
                          <div className="onboarding-right__testimonial-stars" aria-hidden="true">
                            {STAR_RATINGS.map(value => (
                              <span key={`star-${card.name}-${card.company}-${value}`} role="presentation">
                                ★
                              </span>
                            ))}
                          </div>
                          <p className="onboarding-right__testimonial-quote m-0">“{card.quote}”</p>
                          <div className="onboarding-right__testimonial-meta mt-2">
                            <p className="mb-0">{card.name}</p>
                            <p className="mb-0">
                              {card.role}
                              {card.company ? `, ${card.company}` : ''}
                            </p>
                          </div>
                        </article>
                      ))}

                      {Array.from({ length: placeholderCount }, (_, index) => (
                        <article
                          key={`placeholder-${pageIndex}-${index}`}
                          className="onboarding-right__testimonial onboarding-right__testimonial-placeholder"
                          aria-hidden="true"
                        />
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={handleNextTestimonial}
                      aria-label="Testimonio siguiente"
                      className="onboarding-right__carousel-button"
                    >
                      ›
                    </button>
                  </div>
                </div>
              </div>
            </Col>
          )}
        </Row>
      </div>
    </div>
  );
};

// const initialValues = {
//   firstname: '',
//   lastname: '',
//   email: '',
//   phone: '',
//   serviceTier: 1
// };

const validationSchema = Yup.object().shape({
  firstname: Yup.string().required('Requerido'),
  lastname: Yup.string().required('Requerido'),
  phone: Yup.string()
    .required('Requerido')
    .min(8, 'Debe ser mayor a 8 carácteres')
    .max(15, 'Debe ser menor a 15'),
  email: Yup.string()
    .email('Email inválido')
    .required('Requerido'),
  serviceTier: Yup.number()
    .required('Requerido')
    .oneOf([0, 1, 2], 'Debe seleccionar una opción válida')
});

const mapStateToProps = state => ({
  account: state.account
});

export default withRouter(
  connect(mapStateToProps)(
    withFormik({
      mapPropsToValues: props => {
        let serviceTier = 1;
        const account = props.account || {};
        const { serviceTier: accountServiceTier } = account;

        if (props.location?.state?.isAccounting) {
          serviceTier = 0;
        } else if (props.location?.state?.isFreemium) {
          serviceTier = 3;
        } else if (accountServiceTier !== undefined) {
          serviceTier = accountServiceTier;
        }

        return {
          firstname: props.account?.firstname || '',
          lastname: props.account?.lastname || '',
          email: props.account?.email || loadOnboardingPaymentContext()?.email || '',
          phone: props.account?.phone || '',
          serviceTier
        };
      },
      validationSchema
    })(AccountRegister)
  )
);
